// EnhancedFTANode.tsx
// Renders all AIAG-VDA/IEC 61025 FTA + Block Diagram node shapes.
// Supports 'basic' (monochrome/engineering) and 'colorful' (vivid) display modes.

import { memo } from 'react';
import { Handle, Position, NodeProps } from 'reactflow';
import clsx from 'clsx';
import { FTANodeData, FTANodeType } from '../../types/fta.types';
import * as S from './FTAShapes';

// ── Palette: basic (greyscale) vs colorful ───────────────────────────────────

type ColorSet = { fill: string; stroke: string; labelBg: string; handleColor: string; };

const COLORFUL: Record<string, ColorSet> = {
    [FTANodeType.TOP_EVENT]:           { fill: '#fee2e2', stroke: '#dc2626', labelBg: '#dc2626', handleColor: '#dc2626' },
    [FTANodeType.INTERMEDIATE_EVENT]:  { fill: '#dbeafe', stroke: '#2563eb', labelBg: '#2563eb', handleColor: '#2563eb' },
    [FTANodeType.BASIC_EVENT]:         { fill: '#dcfce7', stroke: '#16a34a', labelBg: '#16a34a', handleColor: '#16a34a' },
    [FTANodeType.UNDEVELOPED_EVENT]:   { fill: '#fef9c3', stroke: '#ca8a04', labelBg: '#ca8a04', handleColor: '#ca8a04' },
    [FTANodeType.AND_GATE]:            { fill: '#dbeafe', stroke: '#1d4ed8', labelBg: '#1d4ed8', handleColor: '#1d4ed8' },
    [FTANodeType.OR_GATE]:             { fill: '#fef3c7', stroke: '#d97706', labelBg: '#d97706', handleColor: '#d97706' },
    'XOR_GATE':                        { fill: '#fee2e2', stroke: '#dc2626', labelBg: '#dc2626', handleColor: '#dc2626' },
    'INHIBIT_GATE':                    { fill: '#f5f3ff', stroke: '#7c3aed', labelBg: '#7c3aed', handleColor: '#7c3aed' },
    'PAND_GATE':                       { fill: '#ecfdf5', stroke: '#059669', labelBg: '#059669', handleColor: '#059669' },
    'CONDITIONING_EVENT':              { fill: '#f3e8ff', stroke: '#9333ea', labelBg: '#9333ea', handleColor: '#9333ea' },
    'HOUSE_EVENT':                     { fill: '#e0f2fe', stroke: '#0284c7', labelBg: '#0284c7', handleColor: '#0284c7' },
    'EXTERNAL_EVENT':                  { fill: '#f0fdf4', stroke: '#15803d', labelBg: '#15803d', handleColor: '#15803d' },
    'TRANSFER_IN':                     { fill: '#f8fafc', stroke: '#475569', labelBg: '#475569', handleColor: '#475569' },
    'TRANSFER_OUT':                    { fill: '#f8fafc', stroke: '#475569', labelBg: '#475569', handleColor: '#475569' },
    'BLOCK_FUNCTION':                  { fill: '#eff6ff', stroke: '#3b82f6', labelBg: '#3b82f6', handleColor: '#3b82f6' },
    'BLOCK_PROCESS':                   { fill: '#f0fdf4', stroke: '#22c55e', labelBg: '#22c55e', handleColor: '#22c55e' },
    'BLOCK_DECISION':                  { fill: '#fefce8', stroke: '#eab308', labelBg: '#a16207', handleColor: '#eab308' },
    'BLOCK_CONNECTOR':                 { fill: '#f0f9ff', stroke: '#0ea5e9', labelBg: '#0284c7', handleColor: '#0ea5e9' },
};

const BASIC: Record<string, ColorSet> = Object.fromEntries(
    Object.keys(COLORFUL).map(k => [k, { fill: '#f8fafc', stroke: '#334155', labelBg: '#334155', handleColor: '#64748b' }])
);

function getColors(mode: string, type: string): ColorSet {
    const palette = mode === 'basic' ? BASIC : COLORFUL;
    return palette[type] || palette[FTANodeType.INTERMEDIATE_EVENT];
}

// ── Shape renderer ────────────────────────────────────────────────────────────

function RenderShape({ type, fill, stroke }: { type: string; fill: string; stroke: string }) {
    const p = { fill, stroke };
    switch (type) {
        case FTANodeType.TOP_EVENT:          return <S.EventTop {...p} />;
        case FTANodeType.INTERMEDIATE_EVENT: return <S.EventIntermediate {...p} />;
        case FTANodeType.BASIC_EVENT:        return <S.EventBasic {...p} />;
        case FTANodeType.UNDEVELOPED_EVENT:  return <S.EventUndeveloped {...p} />;
        case 'CONDITIONING_EVENT':           return <S.EventConditioning {...p} />;
        case 'HOUSE_EVENT':                  return <S.EventHouse {...p} />;
        case 'EXTERNAL_EVENT':               return <S.EventExternal {...p} />;
        case FTANodeType.AND_GATE:           return <S.GateAND {...p} />;
        case FTANodeType.OR_GATE:            return <S.GateOR {...p} />;
        case 'XOR_GATE':                     return <S.GateXOR {...p} />;
        case 'INHIBIT_GATE':                 return <S.GateINHIBIT {...p} />;
        case 'PAND_GATE':                    return <S.GatePAND {...p} />;
        case 'TRANSFER_IN':                  return <S.TransferIn {...p} />;
        case 'TRANSFER_OUT':                 return <S.TransferOut {...p} />;
        case 'BLOCK_FUNCTION':               return <S.BlockFunction {...p} />;
        case 'BLOCK_PROCESS':                return <S.BlockProcess {...p} />;
        case 'BLOCK_DECISION':               return <S.BlockDecision {...p} />;
        case 'BLOCK_CONNECTOR':              return <S.BlockConnector {...p} />;
        default:                             return <S.EventIntermediate {...p} />;
    }
}

const GATE_TYPES = new Set([
    FTANodeType.AND_GATE, FTANodeType.OR_GATE,
    'XOR_GATE', 'INHIBIT_GATE', 'PAND_GATE'
]);

const HEIGHT: Record<string, number> = {
    [FTANodeType.BASIC_EVENT]: 56,
    'CONDITIONING_EVENT': 44,
    'HOUSE_EVENT': 64,
    'EXTERNAL_EVENT': 56,
    'BLOCK_CONNECTOR': 56,
    'BLOCK_DECISION': 44,
    default: 52,
};

function getHeight(type: string) { return HEIGHT[type] ?? HEIGHT.default; }
function getWidth(type: string) {
    if (['BLOCK_FUNCTION', 'BLOCK_PROCESS', FTANodeType.TOP_EVENT].includes(type)) return 144;
    if (['BLOCK_DECISION'].includes(type)) return 120;
    if ([FTANodeType.BASIC_EVENT, 'BLOCK_CONNECTOR', 'EXTERNAL_EVENT', 'CONDITIONING_EVENT'].includes(type)) return 64;
    return 96;
}

const EnhancedFTANode = ({ data, selected }: NodeProps<FTANodeData & { displayMode?: string }>) => {
    const { type, label, probability, calculatedProbability, displayMode = 'colorful' } = data;
    const colors = getColors(displayMode, type);
    const isGate = GATE_TYPES.has(type);
    const isTopEvent = type === FTANodeType.TOP_EVENT;
    const w = getWidth(type);
    const h = getHeight(type);

    const displayProb = probability !== undefined
        ? probability.toExponential(2)
        : calculatedProbability !== undefined
            ? calculatedProbability.toExponential(2)
            : null;

    return (
        <div className="relative flex flex-col items-center select-none" style={{ width: w + 32 }}>
            {/* Target handle — all except top event */}
            {!isTopEvent && (
                <Handle type="target" position={Position.Top}
                    style={{ background: colors.handleColor, width: 8, height: 8, border: 'none' }} />
            )}

            {/* Shape */}
            <div
                className={clsx('transition-all', selected && 'drop-shadow-xl')}
                style={{
                    width: w, height: h,
                    filter: selected ? `drop-shadow(0 0 6px ${colors.stroke}99)` : undefined,
                    transform: selected ? 'scale(1.04)' : undefined,
                }}
            >
                <RenderShape type={type} fill={colors.fill} stroke={colors.stroke} />
            </div>

            {/* Label */}
            <div
                className={clsx(
                    'mt-1 px-2 py-0.5 text-[11px] font-semibold text-white rounded-sm text-center max-w-[160px] break-words leading-tight',
                    selected && 'ring-2 ring-offset-1 ring-white'
                )}
                style={{ backgroundColor: colors.labelBg }}
            >
                {label}
            </div>

            {/* Probability badge */}
            {displayProb && (
                <div className="mt-0.5 px-1.5 py-0.5 text-[9px] font-mono bg-slate-800 text-white rounded-sm">
                    P={displayProb}
                </div>
            )}

            {/* Source handle — events and gates that have outputs */}
            {(isTopEvent || type === FTANodeType.INTERMEDIATE_EVENT || isGate ||
              ['CONDITIONING_EVENT', 'HOUSE_EVENT', 'EXTERNAL_EVENT', 'BLOCK_FUNCTION', 'BLOCK_PROCESS', 'BLOCK_DECISION', 'BLOCK_CONNECTOR'].includes(type)) && (
                <Handle type="source" position={Position.Bottom}
                    style={{ background: colors.handleColor, width: 8, height: 8, border: 'none' }} />
            )}
        </div>
    );
};

export default memo(EnhancedFTANode);
